/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { REPORT_SCH_TEMPLATE, ReportService } from '@gh/builder-shared';
import {
  reportField,
  reportIdField,
  SCHEDULE_TYPE_CONSTRUCTION,
  SCHEDULE_TYPE_MAINTENANCE,
  SCHEDULE_TYPE_PRE_CONSTRUCTION,
  SCHEDULE_TYPE_SALES,
  ScheduleTaskType,
  ScheduleTemplateListFilter,
  ScheduleTemplateRestService,
  ScheduleType,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteParam,
  getRouteQueryParam,
  getRouteStaticData,
  InitDialogViewService,
  InitViewService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';

import { assign } from 'lodash';

import { Observable } from 'rxjs';

import {
  ScheduleTemplateCheckUsage,
  ScheduleTemplateDetails,
  scheduleTemplateDetailsFromDto,
  scheduleTemplateDetailsToDto,
  ScheduleTemplateDialogConfig,
  ScheduleTemplateListItem,
} from './scheduling-schedule-template.model';

@Injectable()
export class SchedulingScheduleTemplateBusinessService
  implements InitViewService, InitDialogViewService<ScheduleTemplateDialogConfig> {

  constructor(private gridLayout: GridLayoutService,
              private dictionaryService: DictionaryService,
              private restService: ScheduleTemplateRestService,
              private reportService: ReportService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const type = getRouteStaticData(route)['type'];
    return {
      gridLayout: this.gridLayout.get(type === ScheduleType.PreConstruction
        ? 'preconstructionScheduleTemplates'
        : 'scheduleTemplates'),
      scheduleTemplates: this.findAll({ type }),
    };
  }

  initDialogView(route: DialogRouteSnapshot<ScheduleTemplateDialogConfig>): any {
    return {
      type: route.input.type,
    };
  }

  initScheduleTemplateDetailsView(route: ActivatedRouteSnapshot): any {
    const cloneScheduleTemplateId = getRouteQueryParam(route, 'cloneScheduleTemplate');
    const templateId = getRouteParam(route, 'templateId');
    const scheduleTemplateType = getRouteStaticData(route)['type'];
    return {
      gridLayout: this.gridLayout.get(scheduleTemplateType === ScheduleType.Construction ?
        'constructionScheduleTemplateTasks'
        : 'scheduleTemplateTasks'),
      scheduleTemplateTaskType: this.dictionaryService.getByEnum(ScheduleTaskType),
      scheduleTypeDictionary: this.dictionaryService.getByEnum(ScheduleType),
      scheduleTemplate: templateId
        ? this.get(templateId)
        : cloneScheduleTemplateId
          ? this.getClone(cloneScheduleTemplateId)
          : this.init({ type: scheduleTemplateType }),
      reportUrl: this.reportService.generateUrl(REPORT_SCH_TEMPLATE, [
        reportIdField('TemplateId', templateId),
        reportField('ScheduleType', {
          [SCHEDULE_TYPE_CONSTRUCTION]: 1,
          [SCHEDULE_TYPE_SALES]: 2,
          [SCHEDULE_TYPE_PRE_CONSTRUCTION]: 3,
          [SCHEDULE_TYPE_MAINTENANCE]: 4,
        }[scheduleTemplateType]),
      ]),
    };
  }

  init(filter: ScheduleTemplateListFilter): Observable<ScheduleTemplateDetails> {
    return this.restService.init(filter).pipe(map$(scheduleTemplateDetailsFromDto));
  }

  findAll(filter: ScheduleTemplateListFilter): Observable<ScheduleTemplateListItem[]> {
    return this.restService.findAll(filter);
  }

  findAllByOffice(officeId: ID, filter?: ScheduleTemplateListFilter): Observable<ScheduleTemplateListItem[]> {
    return this.restService.findAllByOffice(officeId, filter);
  }

  get(templateId: ID): Observable<ScheduleTemplateDetails> {
    return this.restService.get(templateId).pipe(map$(scheduleTemplateDetailsFromDto));
  }

  getClone(templateId: ID): Observable<ScheduleTemplateDetails> {
    return this.restService.get(templateId).pipe(
      map$(
      (scheduleTemplate: ScheduleTemplateDetails) => assign(scheduleTemplate, {
        templateId: void 0,
        name: void 0,
      })),
      map$(scheduleTemplateDetailsFromDto));
  }

  save(scheduleTemplate: ScheduleTemplateDetails): Observable<ScheduleTemplateDetails> {
    return scheduleTemplate.templateId
      ? this.update(scheduleTemplate)
      : this.create(scheduleTemplate);
  }

  update(scheduleTemplate: ScheduleTemplateDetails): Observable<ScheduleTemplateDetails> {
    return this.restService.update(scheduleTemplateDetailsToDto(scheduleTemplate)).pipe(
      map$(scheduleTemplateDetailsFromDto));
  }

  create(scheduleTemplate: ScheduleTemplateDetails): Observable<ScheduleTemplateDetails> {
    return this.restService.create(scheduleTemplateDetailsToDto(scheduleTemplate)).pipe(
      map$(scheduleTemplateDetailsFromDto));
  }

  recalculate(scheduleTemplate: ScheduleTemplateDetails): Observable<ScheduleTemplateDetails> {
    return this.restService.recalculate(scheduleTemplateDetailsToDto(scheduleTemplate)).pipe(
      map$(scheduleTemplateDetailsFromDto));
  }

  checkUsage(scheduleTemplate: ScheduleTemplateListItem): Observable<ScheduleTemplateCheckUsage> {
    return this.restService.checkUsage(scheduleTemplate);
  }

  deactivateOrDelete(scheduleTemplate: ScheduleTemplateListItem,
                     checkUsage: ScheduleTemplateCheckUsage): Observable<any> {
    return this.restService.deactivateOrDelete(scheduleTemplate, checkUsage);
  }

  activate(scheduleTemplate: ScheduleTemplateListItem): Observable<ScheduleTemplateListItem> {
    return this.restService.activate(scheduleTemplate);
  }

  setDefaultForWorksheet(scheduleTemplate: ScheduleTemplateListItem): Observable<void> {
    return this.restService.setDefaultForWorksheet(scheduleTemplate);
  }

  checkDelete(scheduleTemplate: ScheduleTemplateDetails, templateTaskId: ID): Observable<void> {
    return this.restService.checkDelete(scheduleTemplate, templateTaskId);
  }
}
